import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

function CookiePolicy() {
  const { t } = useTranslation('cookie-policy');
  const { lang } = useParams<{ lang: string }>();

  return (
    <>
      <Helmet>
        <title>Cookie Policy - ShiftPic</title>
        <meta name="description" content="ShiftPic's cookie policy explains how we use cookies and similar technologies to improve your experience while using our free image converter." />
        <meta name="keywords" content="ShiftPic cookies, cookie policy, privacy, tracking, analytics, user preferences, GDPR compliance, cookie consent, browser cookies, website tracking" />
        <link rel="canonical" href={`https://shiftpic.com/${lang || 'en'}/cookie-policy/`} />
        <meta property="og:title" content="Cookie Policy - ShiftPic" />
        <meta property="og:description" content="ShiftPic's cookie policy explains how we use cookies and similar technologies to improve your experience while using our free image converter." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://shiftpic.com/${lang || 'en'}/cookie-policy/`} />
        <meta property="og:image" content="https://shiftpic.com/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Cookie Policy - ShiftPic" />
        <meta name="twitter:description" content="ShiftPic's cookie policy explains how we use cookies and similar technologies to improve your experience while using our free image converter." />
        <meta name="twitter:image" content="https://shiftpic.com/og-image.jpg" />
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="mt-8 mb-16">
            <h1>{t('title')}</h1>
            <p>{t('lastUpdated', { date: new Date().toLocaleDateString() })}</p>
          </div>
              
          <section className="mb-10" aria-labelledby="what-are-cookies">
            <h2 id="what-are-cookies">
              {t('sections.whatAreCookies.title')}
            </h2>
            <p>
              {t('sections.whatAreCookies.description')}
            </p>
          </section>

          <section className="mb-10" aria-labelledby="our-use-of-cookies">
            <h2 id="our-use-of-cookies">
              {t('sections.ourUseOfCookies.title')}
            </h2>
            <p>
              {t('sections.ourUseOfCookies.description')}
            </p>
            <h3>
              {t('sections.ourUseOfCookies.essentialCookies.title')}
            </h3>
            <p>
              {t('sections.ourUseOfCookies.essentialCookies.description')}
            </p>
            <ul className="list-disc pl-6 mb-4">
              {(t('sections.ourUseOfCookies.essentialCookies.items', { returnObjects: true }) as string[]).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </section>

          <section className="mb-10" aria-labelledby="language-preferences">
            <h3 id="language-preferences">
              {t('sections.languagePreferences.title')}
            </h3>
            <p>
              {t('sections.languagePreferences.description')}
            </p>
          </section>

          <section className="mb-10" aria-labelledby="analytics">
            <h2 id="analytics">
              {t('sections.analytics.title')}
            </h2>
            <p>
              {t('sections.analytics.description')}
            </p>
            <p>
              {t('sections.analytics.dataCollected')}
            </p>
            <ul className="list-disc pl-6 mb-4">
              {(t('sections.analytics.items', { returnObjects: true }) as string[]).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
            <p>
              {t('sections.analytics.optOut')}
            </p>
          </section>

          <section className="mb-10" aria-labelledby="no-tracking">
            <h2 id="no-tracking">
              {t('sections.noTracking.title')}
            </h2>
            <p>
              {t('sections.noTracking.description')}
            </p>
            <ul className="list-disc pl-6 mb-4">
              {(t('sections.noTracking.items', { returnObjects: true }) as string[]).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </section>
        </div>
    </>
  );
}

export default CookiePolicy;